/**
 * Attacker/Defender zone mirroring + the derived "ghost" copies of mirrored
 * objectives/terrain. Extracted from MapEditor.vue: this logic is self-contained
 * (it reads/writes the working map and asks the host to commit/deselect) and is
 * the kind of geometry that benefits from living on its own.
 */
import { computed, type Ref } from 'vue';
import {
  type IBattleplanMap,
  type IMapObjective,
  type IMapTerrain,
  type IMapZone,
  ZoneType,
  Mirror,
  mirrorPoint,
} from '@scrolldex/shared';

export interface MapMirrorHost {
  commit: () => void;
  deselect: () => void;
}

/** Mirror one zone (corners + label offset) across the axis, retyped to destType. */
function mirrorZone(z: IMapZone, mode: Mirror, destType: ZoneType): IMapZone {
  const a = mirrorPoint(z.x1, z.y1, mode);
  const b = mirrorPoint(z.x2, z.y2, mode);
  const out: IMapZone = {
    ...z,
    type: destType,
    x1: Math.min(a.x, b.x),
    y1: Math.min(a.y, b.y),
    x2: Math.max(a.x, b.x),
    y2: Math.max(a.y, b.y),
  };
  const flipX = mode === Mirror.X || mode === Mirror.Both;
  const flipY = mode === Mirror.Y || mode === Mirror.Both;
  if (z.labelDx !== undefined) out.labelDx = flipX ? -z.labelDx : z.labelDx;
  if (z.labelDy !== undefined) out.labelDy = flipY ? -z.labelDy : z.labelDy;
  return out;
}

export function useMapMirror(editMap: Ref<IBattleplanMap>, host: MapMirrorHost) {
  // For every objective/terrain with a truthy `mirror`, compute the mirrored
  // position so the editor shows the full layout while storing only originals.
  const ghostObjectives = computed<Array<IMapObjective & { sourceIdx: number }>>(() =>
    editMap.value.objectives.flatMap((obj, i) =>
      obj.mirror ? [{ ...obj, ...mirrorPoint(obj.x, obj.y, obj.mirror), sourceIdx: i }] : [],
    ),
  );

  const ghostTerrain = computed<Array<IMapTerrain & { sourceIdx: number }>>(() =>
    editMap.value.terrain.flatMap((t, i) =>
      t.mirror ? [{ ...t, ...mirrorPoint(t.x, t.y, t.mirror), sourceIdx: i }] : [],
    ),
  );

  const zoneCounts = computed(() => ({
    attacker: editMap.value.zones.filter((z) => z.type === ZoneType.Attacker).length,
    defender: editMap.value.zones.filter((z) => z.type === ZoneType.Defender).length,
  }));

  function applyZoneMirror({ mode, from }: { mode: Mirror; from: ZoneType }) {
    const dest = from === ZoneType.Attacker ? ZoneType.Defender : ZoneType.Attacker;
    const sources = editMap.value.zones.filter((z) => z.type === from);
    // Keep everything except the destination zones (which are replaced), then add
    // the mirrored copies of the source zones as the destination type.
    const kept = editMap.value.zones.filter((z) => z.type !== dest);
    const mirrored = sources.map((z) => mirrorZone(z, mode, dest));
    editMap.value = { ...editMap.value, zones: [...kept, ...mirrored] };
    host.deselect();
    host.commit();
  }

  return { ghostObjectives, ghostTerrain, zoneCounts, applyZoneMirror };
}
